import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { DateFormater, getStatus } from "../../../services/Helper";
import "./style.css";

const OrdersHistory = ({ groupedOrders }) => {
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [expandedMergedOrder, setExpandedMergedOrder] = useState(null);
  const [showOlder, setShowOlder] = useState(false);

  const allOrders = useMemo(
    () => Object.values(groupedOrders).flat().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [groupedOrders]
  );
  const visibleIds = useMemo(() => new Set(allOrders.slice(0, 5).map(o => o._id)), [allOrders]);
  const olderCount = Math.max(0, allOrders.length - 5);

  const toggleZipOrders = (orderId) => {
    setExpandedOrder(prev => prev === orderId ? null : orderId);
  };

  const toggleMergedOrders = (orderId) => {
    setExpandedMergedOrder(prev => prev === orderId ? null : orderId);
  };

  // Helper function to format date as "24 April"
  const formatDateDot = (dateString) => {
    const date = new Date(dateString);
    return {
      day: date.getDate(),
      month: date.toLocaleString('default', { month: 'short' })
    };
  };
  const getRiskMeta = (risk) => {
    if (risk >= 14 && risk <= 27) {
      return {
        label: "Very Low Risk",
        color: "#22c55e",
        tooltipClass: "risk-tooltip-very-low",
        description:
          "Suitable for minimal or no questioning if classified as self-selection (Category D). Otherwise, brief assessment may be required.",
      };
    }

    if (risk >= 28 && risk <= 39) {
      return {
        label: "Low Risk",
        color: "#15803d",
        tooltipClass: "risk-tooltip-low",
        description:
          "May be supplied with minimal questioning. If suitable for self-selection (Category D), questioning may not be required. If Category B, a brief assessment should be undertaken.",
      };
    }

    if (risk >= 40 && risk <= 49) {
      return {
        label: "Moderate Risk",
        color: "#d97706",
        tooltipClass: "risk-tooltip-moderate",
        description: "Requires detailed questions",
      };
    }

    if (risk >= 50 && risk <= 59) {
      return {
        label: "High Risk",
        color: "#ef4444",
        tooltipClass: "risk-tooltip-high",
        description: "Requires detailed questions",
      };
    }

    return {
      label: "Very High Risk",
      color: "#991b1b",
      tooltipClass: "risk-tooltip-very-high",
      description: "Requires detailed questions",
    };
  };


  return (
    <div className="card mb-6 rounded-3 p-4 mt-3 shadow-sm">
      <div className="card-body p-0">
        <h5 className="card-title fs-5 mb-5 text-dark">Orders History</h5>
        {(() => {
          const renderMonthGroup = (monthYear, orders) => (
            <div key={monthYear} className="mb-3">
              <h6 className="text-primary mb-2">{monthYear}</h6>
              <div className="timeline-container">
                {orders.map(order => {
                const formattedDate = formatDateDot(order.createdAt);
                return (
                  <div key={order._id} className="timeline-item mb-3">
                    <div className="timeline-date-dot">
                      <div className="timeline-day">{formattedDate.day}</div>
                      <div className="timeline-month">{formattedDate.month}</div>
                    </div>
                    <div className="timeline-content">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="d-flex align-items-center flex-wrap gap-2">
                          <Link to={`/orders/${order?._id}`} className="fw-medium text-decoration-none text-dark" style={{ fontSize: '14px' }}>
                            {order.orderId}
                          </Link>
                          <span 
                            className={`badge rounded-lg rounded-pill alert py-3 px-4 mb-0 border-0 text-capitalize ${getStatus(order.status, order.isPGD).class}`}
                            style={{ fontSize: '9px' }}
                          >
                            {getStatus(order?.status, order?.isPGD).text}
                          </span>
                        </div>
                        
                        <div className="d-flex align-items-center">
                          {/* Ship To section */}
                          {order.zipOrders?.length > 0 && (
                            <div 
                              className="merged-toggle text-primary small fw-semibold cursor-pointer mt-2 mb-1 d-inline-block"
                              onClick={() => toggleZipOrders(order._id)}
                            >
                              <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                              <span>{order.shippingInfo.zip}</span>
                              <i className={`ms-2 fas fa-chevron-${expandedOrder === order._id ? 'up' : 'down'}`}></i>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Products list */}
                      <div className="products-list mb-2">
                        {order?.products.map((pro, index) => {
                          const riskMeta = pro.riskLevel != null ? getRiskMeta(pro.riskLevel) : null;

                          return (
                          <div key={index} className="product-info-wrapper">
                            {/* Product Name */}
                            <div className="small text-muted product-name" title={`${pro?.name} (${pro?.variation})`}>
                              {pro?.name} 
                              {pro.variation && <span className='text-primary'>({pro?.variation})</span>}  
                              <span className="text-danger"> ×{pro?.quantity}</span>
                              {riskMeta && (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip className={riskMeta.tooltipClass}>
                                      <strong>{riskMeta.label} ({pro.riskLevel})</strong>
                                      <div className="mt-1" style={{ textAlign: 'left' }}>{riskMeta.description}</div>
                                    </Tooltip>
                                  }
                                >
                                  <span className="badge" style={{ cursor: 'pointer', backgroundColor: riskMeta.color, color: '#fff' }}>
                                    <i className="fas fa-exclamation-triangle me-1"></i>
                                    Risk: {pro.riskLevel}
                                  </span>
                                </OverlayTrigger>
                              )}
                            </div>

                            {/* Hover Content */}
                            <div className="pro-hover-info d-flex gap-2 flex-wrap mt-1">
                              
                              {pro.purchaseLimitDays != null && (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip className="limit-tooltip">
                                      <strong>Purchase Limit</strong>
                                      <div className="mt-1" style={{ textAlign: 'left' }}>Number of days within which the customer is reasonably allowed to repurchase the medication (per quantity purchased).</div>
                                    </Tooltip>
                                  }
                                >
                                  <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle" style={{ cursor: 'pointer' }}>
                                    <i className="fas fa-calendar-alt me-1"></i>Limit: {pro.purchaseLimitDays}d
                                  </span>
                                </OverlayTrigger>
                              )}

                              {pro.variationLimit != null && (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip className="vlimit-tooltip">
                                      <strong>Variation Limit</strong>
                                      <div className="mt-1" style={{ textAlign: 'left' }}>The max quantity purchase limit for the product.</div>
                                    </Tooltip>
                                  }
                                >
                                  <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle" style={{ cursor: 'pointer' }}>
                                    <i className="fas fa-box me-1"></i>V.Limit: {Math.floor(pro.variationLimit)}
                                  </span>
                                </OverlayTrigger>
                              )}
                            </div>
                          </div>
                        )})}
                      </div>

                      {/* ZIP Orders section */}
                      {expandedOrder === order._id && order.zipOrders?.length > 0 && (
                        <div className="zip-orders-container mt-2 ps-2 border-start border-primary" style={{background:"#f8ef97", padding:'5px'}}>
                          <h6 className="text-muted small mb-2">Other orders with same postcode</h6>
                          <div className="zip-orders-grid">
                            {order.zipOrders.map(zipOrder => (
                              <div key={zipOrder._id} className="zip-order-card mb-2 bg-light rounded shadow-sm p-2">
                                <div className="text-muted fs-9 mb-1">
                                  {DateFormater(zipOrder?.createdAt)}
                                </div>
                                <div className="d-flex justify-content-between align-items-center">
                                  <Link to={`/orders/${zipOrder._id}`} className="fs-9 fw-semibold text-decoration-none">
                                    {zipOrder.orderId} <span className="text-primary">({zipOrder.buyerInfo.username})</span>
                                  </Link>
                                  <span 
                                    className={`text-capitalize small fs-9`}
                                  >
                                    {getStatus(zipOrder.status, zipOrder.isPGD).text}
                                  </span>
                                </div>
                                <div className="small text-truncate fs-9 text-muted">
                                  {zipOrder.products[0]?.title}
                                  {zipOrder.products.length > 1 && ` +${zipOrder.products.length - 1} more`}
                                </div>
                                <div className="text-muted fs-9">
                                  {zipOrder.shippingInfo.addressLine1}, {zipOrder.shippingInfo.city}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Merged Orders toggle */}
                      {order.isMerged && (
                        <>
                          <div 
                            className="merged-toggle text-primary small fw-semibold cursor-pointer mt-2 mb-1 d-inline-block"
                            onClick={() => toggleMergedOrders(order._id)}
                          >
                            <i className="fas fa-link me-1"></i> Merged Orders
                            <i className={`ms-2 fas fa-chevron-${expandedMergedOrder === order._id ? 'up' : 'down'}`}></i>
                          </div>

                          {expandedMergedOrder === order._id && order.mergedOrderIds?.length > 0 && (
                            <div className="merged-orders-container mt-2 ps-2 border-start border-success">
                              {/* <h6 className="text-muted small mb-2">Merged orders ({order.mergedOrderIds.length})</h6> */}
                              <div className="merged-orders-grid">
                                <div className="merged-order-card mb-2 bg-light rounded shadow-sm p-2">
                                  {order.mergedOrderIds.map((mergedId, index) => (
                                    <div key={index} className="d-flex justify-content-between align-items-center">
                                      <p className="mb-0 small text-muted">{mergedId}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
                })}
              </div>
            </div>
          );

          return (
            <>
              {Object.keys(groupedOrders).map(monthYear => {
                const visible = groupedOrders[monthYear].filter(o => visibleIds.has(o._id));
                return visible.length > 0 ? renderMonthGroup(monthYear, visible) : null;
              })}

              {olderCount > 0 && (
                <div style={{ borderTop: '1px dashed #e8e8e8', textAlign: 'center', margin: '2px 0' }}>
                  <button
                    onClick={() => setShowOlder(p => !p)}
                    style={{ background: 'none', border: 'none', color: '#c8c8c8', fontSize: '11px', cursor: 'pointer', padding: '3px 0', letterSpacing: '0.3px' }}
                  >
                    {showOlder ? '↑ show less' : `↓ ${olderCount} older order${olderCount !== 1 ? 's' : ''}`}
                  </button>
                </div>
              )}

              {showOlder && Object.keys(groupedOrders).map(monthYear => {
                const older = groupedOrders[monthYear].filter(o => !visibleIds.has(o._id));
                return older.length > 0 ? renderMonthGroup(monthYear, older) : null;
              })}
            </>
          );
        })()}

        {/* Styles */}
        <style jsx>{`
          .timeline-container {
            position: relative;
            padding-left: 40px;
          }
          .timeline-item {
            position: relative;
            padding-bottom: 15px;
          }
          .timeline-date-dot {
            position: absolute;
            left: -40px;
            top: 50%;
            transform: translateY(-50%);
            width: 35px;
            text-align: center;
            background-color: #f8f9fa;
            border-radius: 6px;
            padding: 2px;
            border: 1px solid #dee2e6;
          }
          .timeline-day {
            font-weight: bold;
            font-size: 13px;
            line-height: 1;
          }
          .timeline-month {
            font-size: 10px;
            font-weight: 700;
            text-transform: uppercase;
            color: #d02142;
            line-height: 1;
          }
          .timeline-content {
            padding-left: 14px;
            border-left: 2px dashed #e1e1e1;
          }
          .zip-orders-grid,
          .merged-orders-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
            gap: 10px;
          }
          .zip-order-card,
          .merged-order-card {
            transition: all 0.2s ease;
            border: 1px solid #e3e3e3;
          }
          .zip-order-card:hover,
          .merged-order-card:hover {
            background-color: #f8f9fa !important;
          }
          .ship-to-toggle:hover,
          .merged-toggle:hover {
            background-color: #e9f5ff;
            transform: scale(1.02);
          }
          .alert {
            --bs-alert-padding-x: 0.5rem;
            --bs-alert-padding-y: 0.2rem;
          }
          .fs-9 {
            font-size: 9px !important;
          }
        `}</style>
      </div>
    </div>
  );
};

export default OrdersHistory;
